<script lang="ts">
  export let data: Record<string, any> = {};


  $: metadata = data;

  let searchTerm = '';
  let expandedKeys = new Set<string>();

  function toggleKey(key: string) {
    if (expandedKeys.has(key)) {
      expandedKeys.delete(key);
    } else {
      expandedKeys.add(key);
    }
    expandedKeys = new Set(expandedKeys);
  }

  function expandAll() {
    Object.keys(metadata).forEach(key => expandedKeys.add(key));
    expandedKeys = new Set(expandedKeys);
  }

  function collapseAll() {
    expandedKeys.clear();
    expandedKeys = new Set(expandedKeys);
  }

  $: filteredMetadata = searchTerm
    ? Object.fromEntries(
        Object.entries(metadata).filter(([key, value]) =>
          key.toLowerCase().includes(searchTerm.toLowerCase()) ||
          JSON.stringify(value).toLowerCase().includes(searchTerm.toLowerCase())
        )
      )
    : metadata;

  $: metadataKeys = Object.keys(filteredMetadata);
  $: metadataCount = metadataKeys.length;

  function getValueDisplay(value: any): string {
    if (value === null) return 'null';
    if (value === undefined) return 'undefined';
    
    // Handle different types of metadata values
    if (Array.isArray(value)) {
      if (value.length === 0) return '[]';
      return JSON.stringify(value, null, 2);
    }
    
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return String(value);
      }
    }
    
    if (typeof value === 'number') {
      return value.toString();
    }
    
    if (typeof value === 'boolean') {
      return value.toString();
    }
    
    if (typeof value === 'string') {
      // Don't show very long strings as single lines
      if (value.length > 200) {
        return value.substring(0, 200) + '...';
      }
      return value;
    }
    
    return String(value);
  }

  function getValueType(value: any): string {
    if (value === null) return 'null';
    if (value === undefined) return 'undefined';
    if (Array.isArray(value)) return 'array';
    if (typeof value === 'object') return 'object';
    return typeof value;
  }

  function getValueTypeClass(value: any): string {
    const type = getValueType(value);
    switch (type) {
      case 'number': return 'value-type number';
      case 'boolean': return 'value-type boolean';
      case 'string': return 'value-type string';
      case 'object': return 'value-type object';
      case 'array': return 'value-type array';
      case 'null': return 'value-type null';
      case 'undefined': return 'value-type undefined';
      default: return 'value-type';
    }
  }

  function isExpandable(value: any): boolean {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }
</script>

<div class="metadata-tree">
  <div class="metadata-header">
    <h3 class="metadata-title">
      Extracted Metadata
      <span class="metadata-count">({metadataCount})</span>
    </h3>
    <div class="metadata-controls">
      <input
        type="text"
        placeholder="Search metadata..."
        bind:value={searchTerm}
        class="search-input"
      />
      <button
        on:click={expandAll}
        class="control-button"
        disabled={metadataCount === 0}
      >
        Expand All
      </button>
      <button
        on:click={collapseAll}
        class="control-button"
        disabled={metadataCount === 0}
      >
        Collapse All
      </button>
    </div>
  </div>

  <div class="metadata-content">
    {#if metadataCount === 0}
      <div class="empty-state">
        <p>No metadata available</p>
      </div>
    {:else}
      <div class="metadata-items">
        {#each metadataKeys as key (key)}
          <div class="metadata-item">
            <div
              class="metadata-key"
              class:expandable={isExpandable(filteredMetadata[key])}
              role="button"
              tabindex="0"
              on:click={() => isExpandable(filteredMetadata[key]) && toggleKey(key)}
              on:keydown={(e) => isExpandable(filteredMetadata[key]) && (e.key === 'Enter' || e.key === ' ') && toggleKey(key)}
            >
              {#if isExpandable(filteredMetadata[key])}
                <span class="expand-icon">
                  {expandedKeys.has(key) ? '▼' : '▶'}
                </span>
              {:else}
                <span class="expand-icon">•</span>
              {/if}
              <span class="key-text">{key}</span>
            </div>
            <div class="metadata-value" class:expanded={!isExpandable(filteredMetadata[key]) || expandedKeys.has(key)}>
              <div class="value-header">
                <span class="{getValueTypeClass(filteredMetadata[key])}">
                  ({getValueType(filteredMetadata[key])})
                </span>
              </div>
              <pre class="value-content">{getValueDisplay(filteredMetadata[key])}</pre>
            </div>
          </div>
        {/each}
      </div>
    {/if}
  </div>
</div>

<style>
  .metadata-tree {
    @apply bg-white rounded-lg shadow border border-gray-200;
  }

  .metadata-header {
    @apply p-4 border-b border-gray-200;
  }

  .metadata-title {
    @apply text-lg font-semibold text-gray-900 mb-3;
  }

  .metadata-count {
    @apply text-sm font-normal text-gray-500;
  }

  .metadata-controls {
    @apply flex flex-col sm:flex-row gap-2;
  }

  .search-input {
    @apply flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent;
  }

  .control-button {
    @apply px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed;
  }

  .metadata-content {
    @apply max-h-96 overflow-y-auto;
  }

  .empty-state {
    @apply p-8 text-center text-gray-500;
  }

  .metadata-items {
    @apply divide-y divide-gray-100;
  }

  .metadata-item {
    @apply transition-colors hover:bg-gray-50;
  }

  .metadata-key {
    @apply flex items-start p-3 cursor-pointer select-none;
  }

  .metadata-key.expandable:hover {
    @apply bg-gray-100;
  }

  .expand-icon {
    @apply mr-2 text-gray-400 text-xs font-mono mt-1;
  }

  .key-text {
    @apply font-mono text-sm text-gray-900 font-medium;
  }

  .metadata-value {
    @apply border-t border-gray-100;
  }

  .metadata-value:not(.expanded) {
    @apply hidden;
  }

  .metadata-value {
    @apply border-t border-gray-100;
  }



  .value-header {
    @apply px-3 pt-2 pb-1 text-xs;
  }

  .value-type {
    @apply font-mono bg-gray-100 px-2 py-1 rounded;
  }

  .value-content {
    @apply p-3 pt-1 text-sm text-gray-700 bg-gray-50 overflow-x-auto whitespace-pre-wrap font-mono;
    margin: 0;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  }

  .value-type.string { color: #059669; }
  .value-type.number { color: #2563eb; }
  .value-type.boolean { color: #9333ea; }
  .value-type.object { color: #ea580c; }
  .value-type.array { color: #4f46e5; }
  .value-type.null { color: #6b7280; }
  .value-type.undefined { color: #9ca3af; }
</style>